import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFilterMembers } from '@/lib/api/hooks/member'
import type { Member } from '@/lib/api/types'
import getLinkedInUrl from '@/utils/helpers/getLinkedinUrl'

const LeadershipCouncil = () => {
  const [hoveredMember, setHoveredMember] = useState<string | null>(null)
  const navigate = useNavigate()

  // Fetch all members
  const { data: membersData, isLoading, error } = useFilterMembers(
    { page: 1, size: 100 },
    {}
  )

  // Transform API members to component format
  const transformMember = (member: Member) => ({
    id: member.memberId,
    name: member.fullName,
    position: member.memberCategory === 'PRESIDENT' 
      ? 'President' 
      : member.memberCategory === 'VICE_PRESIDENT' 
      ? 'Vice - President' 
      : 'Member',
    country: member.countryName,
    flag: member.flagUrl !== 'N/A' ? member.flagUrl : '',
    image: member.imageUrl,
    linkedin: Array.isArray(member.socialMediaLinks) ? getLinkedInUrl(member.socialMediaLinks) : "",
    biography: member.bio ? [member.bio] : []
  })

  // Group members by category
  const presidentialCouncil = useMemo(() => {
    if (!membersData?.data?.data) return []
    return membersData.data.data
      .filter((m: Member) => m.memberCategory === 'PRESIDENT' || m.memberCategory === 'VICE_PRESIDENT')
      .map(transformMember)
      .sort((a, b) => {
        // Sort: President first, then Vice-President
        if (a.position === 'President') return -1
        if (b.position === 'President') return 1
        return 0
      })
  }, [membersData])

  const members = useMemo(() => {
    if (!membersData?.data?.data) return []
    return membersData.data.data
      .filter((m: Member) => m.memberCategory === 'MEMBER')
      .map(transformMember)
  }, [membersData])

  const handleBiographyClick = (memberId: string) => {
    navigate(`/about/leadership/${memberId}/biography`)
  }

  const MemberCard = ({ member, showBiography = false }: { member: ReturnType<typeof transformMember>, showBiography?: boolean }) => (
    <div className="flex flex-col gap-4 w-80">
      <div 
        className="relative w-full aspect-square group cursor-pointer overflow-hidden"
        onMouseEnter={() => setHoveredMember(member.id)}
        onMouseLeave={() => setHoveredMember(null)}
        onClick={() => handleBiographyClick(member.id)}
      >
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover object-center"
        />
        {(hoveredMember === member.id || showBiography) && (
          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-end justify-center pb-6">
            <button className="px-6 py-2.5 bg-wine-100 text-white text-base font-bold font-satoshi hover:bg-wine-200 transition-colors">
              Biography
            </button>
          </div>
        )}
      </div>
      
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-bold font-satoshi text-gray-100 leading-relaxed">
            {member.name}
          </h3>
          <p className="opacity-70 text-sm font-normal font-satoshi text-gray-100 leading-tight">
            {member.position}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {member.flag && member.flag !== 'N/A' && (
            <img
              src={member.flag}
              alt={`${member.country} flag`}
              className="w-10 h-7 object-cover"
              onError={(e) => {
                // Hide image if it fails to load
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <span className="flex-1 text-sm font-bold font-satoshi text-gray-100 leading-tight">
            {member.country}
          </span>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="bg-surfaceColor-100 py-12 pb-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-wine-100"></div>
            <p className="mt-4 text-sm text-gray-500">Loading leadership council...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-surfaceColor-100 py-12 pb-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <p className="text-sm text-red-500">Error loading leadership council. Please try again later.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-surfaceColor-100 py-12 pb-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-12">
          {/* Presidential Council */}
          {presidentialCouncil.length > 0 && (
            <div className="flex flex-col gap-10">
              <h2 className="text-2xl md:text-3xl font-normal font-pt-serif text-gray-100 leading-loose">
                Presidential Council
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-5 justify-center sm:justify-start items-center sm:items-start">
                {presidentialCouncil.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          )}

          {/* Members */}
          {members.length > 0 && (
            <div className="flex flex-col gap-10">
              <h2 className="text-2xl md:text-3xl font-normal font-pt-serif text-gray-100 leading-loose">
                Members
              </h2>
              
              {/* Mobile: Horizontal scroll */}
              <div className="lg:hidden">
                <div className="flex gap-5 overflow-x-auto pb-4 px-1 scrollbar-hide">
                  {members.map((member) => (
                    <div key={member.id} className="flex-shrink-0">
                      <MemberCard member={member} />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Desktop: Flex wrap */}
              <div className="hidden lg:flex flex-wrap gap-5 justify-start items-start">
                {members.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {presidentialCouncil.length === 0 && members.length === 0 && (
            <div className="text-center py-20">
              <p className="text-sm text-gray-500">No leadership members found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LeadershipCouncil